import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Avatar,
  Breadcrumb,
  Button,
  Card,
  Chip,
  DefinitionList,
  Donut,
  Heatmap,
  Kpi,
  Legend,
  Meter,
  RankedBars,
  RoleChip,
  seriesColor,
  Skeleton,
  StateBlock,
  Tabs,
  TrendChart,
  type Crumb,
  type TabItem,
  type Tone,
} from '@w7/ui'
import { useMemberDetail } from '@/api/hooks/member/useMemberDetail'
import { useMemberTransfers } from '@/api/hooks/member/useMemberTransfers'
import { useCmMe } from '@/api/hooks/useCmMe'
import { useDateRange } from '@/app/range'
import { Amount, AsOf, MiniSpark } from '@/app/bits'
import {
  canCreditTransfer,
  canEditDownline,
  canViewBetList,
  canViewCommission,
  canViewDownline,
  canViewMemberNetExposure,
  canViewPlayerPnl,
  canViewTransferStatement,
  staffCanSeeMemberDetailTab,
} from '@/app/staffAccess'
import { EditDownlinePage } from '@/features/hierarchy/EditDownlinePage'
import { TransferDialog } from '@/features/hierarchy/TransferDialog'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { useQuery } from '@tanstack/react-query'
import {
  ROLE_LABEL,
  isMemberRole,
  parseCurrencyType,
  type HierarchyNode,
} from '@/features/hierarchy/hierarchy'
import {
  BalanceTab,
  BetListTab,
  CommissionTab,
  LabActivityTab,
  LoginHistoryTab,
  TransfersTab,
} from '@/features/member/MemberExtraTabs'
import { MemberStatementTab } from '@/features/member/MemberStatementTab'
import { NetExposureTab } from '@/features/member/NetExposureTab'
import type { MemberAccount, MemberAccountStatus, MemberDetailView } from '@/features/member/memberAccount'
import { availableToGive, limitUsage } from '@/features/member/memberMoney'
import { scrollTabIntoCenter } from '@/features/member/scrollTabIntoCenter'
import {
  count,
  dayLabel,
  EMPTY,
  lll,
  money,
  pct,
  percent,
  sharePoints,
  toneOf,
} from '@/lib/format'
import '@/features/member/member.css'

type TabId =
  | 'overview'
  | 'edit'
  | 'activity'
  | 'balance'
  | 'bets'
  | 'statement'
  | 'transfers'
  | 'login-history'
  | 'commission'
  | 'net-exposure'

const TABS: TabItem<TabId>[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'edit', label: 'Edit' },
  { id: 'activity', label: 'Activity' },
  { id: 'balance', label: 'Balance' },
  { id: 'bets', label: 'Bet List' },
  { id: 'statement', label: 'Account Statement' },
  { id: 'transfers', label: 'Transfer Statements' },
  { id: 'login-history', label: 'Login History' },
  { id: 'commission', label: 'Commission' },
  { id: 'net-exposure', label: 'Net Exposure' },
]

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'))

const isTabId = (value: string | null): value is TabId => TABS.some((tab) => tab.id === value)

function memberDetailTabAllowed(
  id: TabId,
  opts: {
    allowEditDownline: boolean
    allowViewDownline: boolean
    allowBetList: boolean
    allowNetExposure: boolean
    allowPlayerPnl: boolean
    allowTransferStatement: boolean
    allowCommission: boolean
    isPlayer: boolean
  },
): boolean {
  if (id === 'edit') return opts.allowEditDownline
  if (id === 'balance' || id === 'login-history') return opts.allowViewDownline
  if (id === 'bets') return opts.allowBetList
  if (id === 'net-exposure') return opts.isPlayer && opts.allowNetExposure
  if (id === 'statement' || id === 'activity') return opts.allowPlayerPnl
  if (id === 'transfers') return opts.allowTransferStatement
  if (id === 'commission') return opts.allowCommission
  return true
}

const STATUS: Record<MemberAccountStatus, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'info' },
  inactive: { label: 'Inactive', tone: 'muted' },
  suspended: { label: 'Suspended', tone: 'warn' },
  closed: { label: 'Closed', tone: 'muted' },
}

function betLimitLabel(account: MemberAccount): string {
  const ccy = parseCurrencyType(account.currencyType)
  const { min, max } = account.betLimits
  if (!min && !max) return EMPTY
  return `${money(min, { currency: ccy })} – ${money(max, { currency: ccy })}`
}

export function MemberPage() {
  const { id } = useParams<{ id: string }>()
  const { range } = useDateRange()
  const navigate = useNavigate()
  const me = useCmMe()
  const caller = me.data?.caller

  // Fail-closed until /v1/me resolves (owners bypass inside can*).
  const meReady = me.isSuccess
  const allowEditDownline = meReady && canEditDownline(caller)
  const allowViewDownline = meReady && canViewDownline(caller)
  const allowBetList = meReady && canViewBetList(caller)
  const allowNetExposure = meReady && canViewMemberNetExposure(caller)
  const allowPlayerPnl = meReady && canViewPlayerPnl(caller)
  const allowTransferStatement = meReady && canViewTransferStatement(caller)
  const allowCommission = meReady && canViewCommission(caller)
  const allowCredit = meReady && canCreditTransfer(caller)

  const [params, setParams] = useSearchParams()
  const tabParam = params.get('tab')
  const tab: TabId = isTabId(tabParam) ? tabParam : 'overview'
  const tabsRef = useRef<HTMLDivElement>(null)

  const setTab = useCallback(
    (next: TabId) => {
      const nextParams = new URLSearchParams(params)
      if (next === 'overview') nextParams.delete('tab')
      else nextParams.set('tab', next)
      setParams(nextParams, { replace: true })
    },
    [params, setParams],
  )

  const [transferDir, setTransferDir] = useState<'give' | 'take' | null>(null)

  const detailQuery = useMemberDetail(id, { from: range.from, to: range.to })
  const agentQuery = useQuery({
    queryKey: ['cm', 'hierarchy', 'agent', id],
    queryFn: () => cmApi.hierarchyAgent(authHeaders(), id!),
    enabled: Boolean(id),
  })
  const selfQuery = useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
  })

  const detail = detailQuery.data
  const account = detail?.account
  const node = agentQuery.data?.node
  const selfNode = selfQuery.data?.node
  const rootId = selfNode?.id ?? caller?.agentId

  const crumbs = useMemo<Crumb[]>(() => {
    if (!detail || !account) return []
    const start = rootId ? detail.upline.findIndex((a) => a.id === rootId) : -1
    const visible = start >= 0 ? detail.upline.slice(start) : detail.upline
    return [
      ...visible.map((a) => ({
        label: a.username,
        href: a.id === rootId ? '/hierarchy/list' : `/member/${a.id}`,
      })),
      { label: account.username },
    ]
  }, [detail, account, rootId])

  const tabs = useMemo(() => {
    // Until /v1/me lands, only Overview — undefined caller would otherwise look like owner.
    let items = meReady
      ? TABS.filter((t) => staffCanSeeMemberDetailTab(t.id, caller))
      : TABS.filter((t) => t.id === 'overview')
    if (account && !isMemberRole(account.role)) {
      items = items.filter((t) => t.id !== 'net-exposure')
    }
    return items
  }, [account, caller, meReady])

  const activeTab: TabId = memberDetailTabAllowed(tab, {
    allowEditDownline,
    allowViewDownline,
    allowBetList,
    allowNetExposure,
    allowPlayerPnl,
    allowTransferStatement,
    allowCommission,
    isPlayer: Boolean(account && isMemberRole(account.role)),
  })
    ? tab
    : 'overview'

  useEffect(() => {
    if (!me.isSuccess) return
    if (tab === activeTab) return
    setTab(activeTab)
  }, [tab, activeTab, me.isSuccess, setTab])

  useEffect(() => {
    const root = tabsRef.current
    if (!root) return
    const run = () => {
      const scroller = root.querySelector<HTMLElement>('.w7-tabs')
      const selected = root.querySelector<HTMLElement>('.w7-tab[aria-selected="true"]')
      if (!scroller || !selected) return
      scrollTabIntoCenter(scroller, selected)
    }
    const raf = window.requestAnimationFrame(run)
    const t = window.setTimeout(run, 0)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(t)
    }
  }, [activeTab, account?.id, tabs.length])

  if (detailQuery.isLoading || agentQuery.isLoading) {
    return (
      <div className="cm-member-page">
        <Skeleton height={48} />
        <div className="w7-grid w7-grid-6" style={{ marginBottom: 'var(--w7-s5)' }}>
          {Array.from({ length: 6 }, (_, i) => (
            <Kpi
              key={i}
              label={<Skeleton width={78} height={11} />}
              value={<Skeleton width={104} height={26} />}
              meta={<Skeleton width={132} height={11} />}
            />
          ))}
        </div>
        <Skeleton height={200} />
      </div>
    )
  }

  if (!detail || !account || !node) {
    if (detailQuery.isError || agentQuery.isError) {
      const failure = (detailQuery.error ?? agentQuery.error) as Error | null
      return (
        <StateBlock
          icon="warning"
          title="No data found"
          actions={
            <Button
              variant="secondary"
              icon="refresh"
              onClick={() => {
                void detailQuery.refetch()
                void agentQuery.refetch()
              }}
            >
              Try again
            </Button>
          }
        >
          {failure?.message || 'This account could not be reached.'}
        </StateBlock>
      )
    }
    return (
      <StateBlock
        icon="search"
        title="Nothing here"
        actions={
          <Button variant="secondary" onClick={() => navigate('/hierarchy/list')}>
            Browse downline
          </Button>
        }
      >
        This link does not point at anyone in your downline.
      </StateBlock>
    )
  }

  const status = STATUS[account.status]
  const ccy = parseCurrencyType(account.currencyType)
  const free = availableToGive(account.balance, account.exposure)
  const usage = limitUsage(account.exposure, account.creditLimit)
  const canMoveCredit =
    allowCredit && Boolean(selfNode) && Boolean(rootId) && account.parentId === rootId
  const lifetime = detail.lifetime
  const isPlayer = isMemberRole(account.role)
  const rangeTurnover = detail.daily.reduce((sum, d) => sum + d.turnover, 0) || lifetime.turnover
  const rangeBets = detail.daily.reduce((sum, d) => sum + d.bets, 0) || lifetime.bets
  const winRate =
    lifetime.settledBets > 0 && lifetime.wins > 0 ? lifetime.wins / lifetime.settledBets : null
  const memberPnlTone = toneOf(lifetime.memberPnl)
  const dmc = (n: number) => money(n, { currency: ccy, round: true })
  const dm = (n: number) => money(n, { currency: ccy })

  return (
    <div className="cm-member-page">
      <div className="w7-page-head">
        <div className="w7-row" style={{ gap: 'var(--w7-s4)', minWidth: 0, alignItems: 'flex-start' }}>
          <Avatar name={account.displayName || account.username} size="lg" />
          <div style={{ minWidth: 0 }}>
            <div className="w7-row w7-row-wrap" style={{ gap: 8 }}>
              <h1 className="w7-page-title">{account.username}</h1>
              <RoleChip role={account.role} label={ROLE_LABEL[account.role]} />
              <Chip tone={status.tone} size="sm">
                {status.label}
              </Chip>
            </div>
            <p className="w7-card-sub">
              {account.displayName}
              {!isPlayer ? ` · ${account.memberCount} members beneath` : ''}
            </p>
            {crumbs.length > 1 ? (
              <Breadcrumb items={crumbs} onNavigate={(href) => navigate(href)} />
            ) : null}
          </div>
        </div>
        <div
          className="w7-row w7-row-wrap w7-page-head-trail"
          style={{ marginLeft: 'auto', gap: 'var(--w7-s2)' }}
        >
          <AsOf at={detailQuery.dataUpdatedAt || undefined} />
          <Button
            icon="give"
            variant="secondary"
            disabled={!canMoveCredit}
            title={canMoveCredit ? 'Give credit' : 'Only direct upline can give'}
            onClick={() => setTransferDir('give')}
          >
            Give
          </Button>
          <Button
            icon="take"
            variant="secondary"
            disabled={!canMoveCredit}
            title={canMoveCredit ? 'Take credit' : 'Only direct upline can take'}
            onClick={() => setTransferDir('take')}
          >
            Take
          </Button>
        </div>
      </div>

      <div
        className="w7-grid w7-grid-6 w7-member-kpis"
        data-tab={activeTab}
        style={{ marginBottom: 'var(--w7-s5)' }}
      >
        <Kpi
          label="Live balance"
          value={dmc(account.balance)}
          exact={dm(account.balance)}
          accent
          meta={`${dm(free)} of it uncommitted`}
        />
        <Kpi
          label="Total balance"
          value={dmc(account.creditLimit)}
          exact={dm(account.creditLimit)}
          meta={
            isPlayer
              ? crumbs.length > 1
                ? `Set by ${crumbs[crumbs.length - 2]?.label ?? EMPTY}`
                : 'Ceiling this account may hold'
              : 'Credit line assigned to this agent'
          }
        />
        {!isPlayer ? (
          <Kpi
            label="Distributed balance"
            value={dmc(account.distributedBalance)}
            exact={dm(account.distributedBalance)}
            meta={`${dm(Math.max(0, account.creditLimit - account.distributedBalance))} remaining to assign`}
          />
        ) : null}
        <Kpi
          label="Open exposure"
          value={dmc(account.exposure)}
          exact={dm(account.exposure)}
          tone={usage > 1 ? 'loss' : usage > 0.8 ? 'warn' : 'neutral'}
          meta={
            <>
              <Meter value={usage} label={`${percent(usage, 0)} of credit used`} />
              {isPlayer
                ? `${percent(usage, 0)} of the limit`
                : `${percent(usage, 0)} of limit · your share of player bets`}
            </>
          }
        />
        {isPlayer ? (
          <>
            <Kpi
              label="Member P&L"
              value={money(lifetime.memberPnl, { signed: true, round: true, currency: ccy })}
              exact={money(lifetime.memberPnl, { signed: true, currency: ccy })}
              tone={memberPnlTone}
              size="sm"
              meta="In selected range — negative means they lost (book won)."
            />
            <Kpi
              label="Turnover"
              value={money(rangeTurnover, { currency: ccy, round: true })}
              exact={money(rangeTurnover, { currency: ccy })}
              spark={
                detail.daily.length > 1 ? (
                  <MiniSpark values={detail.daily.map((d) => d.turnover)} />
                ) : undefined
              }
              meta={`${count(rangeBets)} bets · ${range.label}`}
            />
            <Kpi
              label="Win rate"
              value={winRate === null ? EMPTY : percent(winRate, 1)}
              meta={
                winRate === null
                  ? 'No bets have settled yet'
                  : `${count(lifetime.wins)} won of ${count(lifetime.settledBets)} settled bets`
              }
            />
          </>
        ) : (
          <>
            <Kpi
              label="Free to give"
              value={dmc(free)}
              exact={dm(free)}
              meta="Live balance less open exposure"
            />
            <Kpi
              label="Members"
              value={count(account.memberCount)}
              meta={`${count(account.directChildren)} direct children`}
            />
          </>
        )}
      </div>

      <div ref={tabsRef} className="w7-member-tabs">
        <Tabs items={tabs} value={activeTab} onChange={setTab} />
      </div>

      <div style={{ marginTop: 'var(--w7-s5)' }}>
        {activeTab === 'overview' ? (
          <OverviewTab
            detail={detail}
            account={account}
            range={range}
            allowCommission={allowCommission}
          />
        ) : null}
        {activeTab === 'edit' && allowEditDownline ? (
          <EditDownlinePage embedId={account.id} />
        ) : null}
        {activeTab === 'activity' && allowPlayerPnl ? <LabActivityTab account={account} /> : null}
        {activeTab === 'balance' && allowViewDownline ? <BalanceTab account={account} /> : null}
        {activeTab === 'bets' && allowBetList ? <BetListTab account={account} /> : null}
        {activeTab === 'statement' && allowPlayerPnl ? (
          <MemberStatementTab
            memberId={account.id}
            username={account.username}
            viewedRole={account.role}
          />
        ) : null}
        {activeTab === 'transfers' && allowTransferStatement ? (
          <TransfersTab account={account} />
        ) : null}
        {activeTab === 'login-history' && allowViewDownline ? (
          <LoginHistoryTab account={account} />
        ) : null}
        {activeTab === 'commission' && allowCommission ? (
          <CommissionTab account={account} />
        ) : null}
        {activeTab === 'net-exposure' && allowNetExposure ? (
          <NetExposureTab account={account} />
        ) : null}
      </div>

      {transferDir && selfNode && node ? (
        <TransferDialog
          self={selfNode as HierarchyNode}
          account={node}
          initialDirection={transferDir}
          onClose={() => setTransferDir(null)}
        />
      ) : null}
    </div>
  )
}

function OverviewTab({
  detail,
  account,
  range,
  allowCommission,
}: {
  detail: MemberDetailView
  account: MemberAccount
  range: { from: string; to: string; label: string }
  allowCommission: boolean
}) {
  const transfersQuery = useMemberTransfers({
    agentId: account.id,
    from: range.from,
    to: range.to,
    pageSize: 6,
    currencyType: account.currencyType,
    partyOnly: true,
  })
  const categories = detail.daily.map((d) => dayLabel(d.date))
  const totalBets = detail.stakeBuckets.reduce((sum, b) => sum + b.count, 0)
  const rangeTurnover = detail.daily.reduce((sum, d) => sum + d.turnover, 0) || detail.lifetime.turnover
  const transfers = transfersQuery.data
  const ccy = parseCurrencyType(account.currencyType)
  const book = (n: number | null | undefined, extra: Parameters<typeof money>[1] = {}) =>
    money(n, { ...extra, currency: ccy })
  const credit = (n: number | null | undefined, extra: Parameters<typeof money>[1] = {}) =>
    money(n, { ...extra, currency: ccy, alreadyDisplay: true })

  return (
    <>
      <div className="w7-grid w7-grid-3" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card
          className="w7-span-2"
          title="Turnover and member P&L"
          subtitle={`Bars are daily matched volume; the line is the member's own settled result · ${range.label}`}
        >
          <TrendChart
            label="Daily turnover with daily member profit and loss"
            categories={categories}
            height={250}
            maxLabels={8}
            format={(v) => book(v, { round: true, symbol: false })}
            formatRight={(v) => book(v, { round: true, symbol: false })}
            series={[
              {
                id: 'turnover',
                label: 'Turnover',
                values: detail.daily.map((d) => d.turnover),
                kind: 'bar',
                color: 'var(--w7-c8)',
                axis: 'right',
              },
              {
                id: 'member-pnl',
                label: 'Member P&L',
                values: detail.daily.map((d) => d.memberPnl),
                kind: 'line',
                color: 'var(--w7-c1)',
              },
            ]}
            emptyMessage="Nothing was placed or settled in this period"
          />
        </Card>

        <Card title="Where they bet" subtitle={`Matched turnover by sport · ${range.label}`}>
          <Donut
            label="Turnover by sport"
            height={190}
            slices={detail.bySport.slice(0, 6).map((s) => ({ label: s.sport, value: s.turnover }))}
            format={(v) => book(v, { round: true })}
            centerValue={book(rangeTurnover, { round: true })}
            centerLabel="turnover"
            emptyMessage="No turnover in this period"
          />
          <Legend
            items={detail.bySport.slice(0, 6).map((s, i) => ({
              label: s.sport,
              color: seriesColor(i),
              value: book(s.turnover, { round: true }),
            }))}
          />
        </Card>
      </div>

      <div className="w7-grid w7-grid-2" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card title="When they bet" subtitle="Every bet ever placed, by weekday and hour of the day">
          <Heatmap
            label="Bets placed by weekday and hour"
            matrix={detail.activity}
            rows={WEEKDAYS}
            columns={HOURS}
            columnStride={3}
            height={200}
            format={(v) => `${count(v)} bets`}
            emptyMessage="No bets have been placed yet"
          />
        </Card>

        <Card
          title="Stake sizes"
          subtitle="Every bet ever placed, by stake band — a member who only ever bets one size shows as a single bar"
        >
          <RankedBars
            label="Bets by stake band"
            rows={detail.stakeBuckets.map((bucket) => ({
              label: bucket.label,
              value: bucket.count,
              color: 'var(--w7-c1)',
              note: totalBets > 0 ? percent(bucket.count / totalBets, 0) : undefined,
            }))}
            format={(v) => count(v)}
            emptyMessage="No bets have been placed yet"
          />
        </Card>
      </div>

      <div className="w7-grid w7-grid-2">
        <Card title="Settings" subtitle="What this account is allowed to do, and on what terms">
          <DefinitionList
            columns={1}
            items={[
              {
                label: ccy === 'INR' ? 'Member Rate' : 'My share',
                value: `${sharePoints(account.share.sportsbook)} / ${sharePoints(account.share.casino)}`,
              },
              ...(allowCommission
                ? [
                    { label: 'Fancy commission', value: pct(account.commission.fancy) },
                    { label: 'Bookmaker commission', value: pct(account.commission.bookmaker) },
                    { label: 'Match odds commission', value: pct(account.commission.matchOdds) },
                  ]
                : []),
              { label: 'Bet limits', value: betLimitLabel(account) },
              { label: 'Created', value: account.createdAt ? lll(account.createdAt) : EMPTY },
              {
                label: 'Last active',
                value: account.lastActiveAt ? lll(account.lastActiveAt) : EMPTY,
              },
            ]}
          />
        </Card>

        <Card
          title="Credit movements"
          subtitle={
            transfers
              ? `${credit(transfers.totals.taken, { round: true })} in, ${credit(transfers.totals.given, { round: true })} out · ${range.label}`
              : range.label
          }
          flush
        >
          {transfersQuery.isLoading ? (
            <div className="w7-col" style={{ gap: 12, padding: 'var(--w7-s4)' }}>
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} height={30} />
              ))}
            </div>
          ) : (transfers?.rows.length ?? 0) === 0 ? (
            <StateBlock icon="transfers" title="No credit has moved in this period">
              Gives and takes between {account.username} and their upline appear here. Use Give or
              Take above to move credit, or widen the date range in the top bar.
            </StateBlock>
          ) : (
            <ul className="w7-list">
              {transfers!.rows.map((t) => {
                const incoming = t.toId === account.id
                const counterparty = incoming ? t.from?.username : t.to?.username
                return (
                  <li key={t.id}>
                    <span className="w7-list-row">
                      <Chip
                        tone={incoming ? 'info' : 'muted'}
                        size="sm"
                        icon={incoming ? 'take' : 'give'}
                      >
                        {incoming ? 'In' : 'Out'}
                      </Chip>
                      <span style={{ minWidth: 0 }}>
                        <b>{incoming ? 'Credit received' : 'Credit returned'}</b>
                        <span className="w7-dim" style={{ display: 'block', fontSize: 12 }}>
                          {lll(t.at)}
                          {counterparty ? ` · ${counterparty}` : ''}
                        </span>
                      </span>
                      <Amount value={t.amount} currency={account.currencyType} />
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>
      </div>
    </>
  )
}
